import { jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';

/**
 * Higher-order component creating a wrapper component that removes the `className` and `style` props from the original component.
 * This is useful when you want to prevent external styles from being applied to a component.
 *
 * @param {React.ComponentType<P>} Component - The original component to wrap.
 * @param {C} [subcomponents] - Optional subcomponents to add to the wrapper component.
 * @returns {React.FC<Omit<P, 'className' | 'style'>> & C} - The wrapper component that removes the `className` and `style` props.
 */
const withoutClassNameStyleProps = (Component, subcomponents) => {
    const WrapperComponent = forwardRef(({ className: _className, style: _style, ...props }, ref) => {
        return jsx(Component, { ...props, ref: ref });
    });
    WrapperComponent.displayName = Component.displayName || Component.name;
    if (subcomponents) {
        Object.entries(subcomponents).forEach(([name, component]) => {
            WrapperComponent[name] = component;
        });
    }
    // @ts-expect-error We are only interested in returned component
    return WrapperComponent;
};

export { withoutClassNameStyleProps as default };
